/**
 * Edit Profile Identity: business-error model + mapper.
 *
 * Same shape as the other form slices: a coarse infra failure (`network` | `api`,
 * from the app-wide `@/utils/infra-error`) OR a business `validation` failure
 * carrying per-field messages.
 *
 * The one flow-specific rule is the 409: the `linkedin_url` column is UNIQUE, so
 * "another profile already uses this URL" is an expected outcome about ONE field
 * and belongs under that field, not in a snackbar.
 */
import { classifyInfraError, type InfraFailure } from '@/utils/infra-error';

import type { ValidationErrors } from './profile-identity-draft.validation';
import { LINKEDIN_URL_TAKEN_MESSAGE } from './profile-identity-draft.validation';

export type EditProfileIdentityError =
  | InfraFailure
  | {
      kind: 'validation';
      fields: ValidationErrors;
      /**
       * Set on a 409 about a merge that did not go all the way (#746): some
       * profiles may have been merged, so the host's data is stale. `intoId`:
       * the profile the edited one went into, when the backend names it.
       */
      merge?: { intoId: string | null };
    };

/** The backend's 409s about a merge (#746) mention it; the plain "URL taken" one does not. */
function isMergeConflict(message: string): boolean {
  return /\bmerg(e|ed|ing)\b/i.test(message);
}

/** The full id in "…merged into profile <id>, but the edit could not be saved…". */
function mergedIntoIdOf(message: string): string | null {
  return /merged into profile ([0-9a-f-]{36})/i.exec(message)?.[1] ?? null;
}

export function toEditProfileIdentityError(e: unknown): EditProfileIdentityError {
  const failure = classifyInfraError(e, 'Failed to update the profile.');
  // A 409 is not an infra problem; it is this field being taken. Turning it into
  // a field error is what puts the message under the LinkedIn URL input.
  if (failure.kind === 'api' && failure.status === 409) {
    // A URL that required a merge which did not complete (#746) says so in its
    // own words: what was merged and what was saved. Any other 409 is the URL taken.
    if (!isMergeConflict(failure.message)) {
      return { kind: 'validation', fields: { linkedinUrl: LINKEDIN_URL_TAKEN_MESSAGE } };
    }
    return {
      kind: 'validation',
      fields: { linkedinUrl: failure.message },
      merge: { intoId: mergedIntoIdOf(failure.message) },
    };
  }
  return failure;
}
